import { useState, type FormEvent } from 'react';
import { ApiError } from '../../api/client';
import { apiErrorMessage } from '../../api/errorMessage.ts';
import { signup } from '../../api/plans';
import { navigate } from '../../app/useRoute';
import { useT } from '../../i18n/i18n.tsx';
import type { MessageKey } from '../../i18n/messages.en.ts';
import { PASSWORD_MIN } from '../account/profile.ts';
import { TurnstileWidget } from '../guest/TurnstileWidget';
import { Button, InlineAlert, TextField } from '../../ui';
import { useSignupOpen } from './useSignupOpen.ts';
import { CheckEmail } from './CheckEmail.tsx';
import './LoginDialog.css';

/** The sign-up wording for the three Turnstile refusals. Not `challengeErrorKey`: its sentence
 * begins "signed in wrong several times", which is not what happened here. */
function signupChallengeKey(code: string | undefined): MessageKey | null {
  if (code === 'CHALLENGE_REQUIRED' || code === 'CHALLENGE_FAILED') return 'auth.signup.challengeRequired';
  if (code === 'CHALLENGE_UNAVAILABLE') return 'auth.signup.challengeUnavailable';
  return null;
}

/**
 * MVP-278. `/signup` — a stranger becomes a shop on the trial plan (spec §4 Web 1). Signed out by
 * definition and mounted in `App.tsx` before the auth branch, like `/invite/<token>`; reuses
 * `LoginDialog.css` for the same centred card.
 *
 * Turnstile the way `LoginDialog` asks it: the widget appears only once the server refuses with a
 * CHALLENGE_* code, because that 403 is where the site key comes from — there is no public route to
 * learn it beforehand. The first press on a Turnstile deployment is therefore the one that reveals
 * the widget; the second, with its token, is the one that signs up.
 *
 * MVP-278 email verification: the new account cannot sign in until its emailed link is opened, so
 * success is "check your email" with a resend button — and so are the two refusals that mean an
 * account is already waiting on this address (EMAIL_SEND_FAILED, SIGNUP_PENDING), with their reason.
 */
export function SignupScreen() {
  const t = useT();
  const [fields, setFields] = useState({ shopName: '', email: '', password: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The address the confirmation link went (or should go) to, once the account exists.
  const [created, setCreated] = useState<string | null>(null);
  const [challengeSiteKey, setChallengeSiteKey] = useState<string | null>(null);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [challengeRound, setChallengeRound] = useState(0);
  // MVP-278 harden: closed when GET /api/signup says so, or when a submit is refused SIGNUP_CLOSED.
  const signupOpen = useSignupOpen();
  const [closedOnSubmit, setClosedOnSubmit] = useState(false);
  const closed = signupOpen === false || closedOnSubmit;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    const email = fields.email.trim();
    try {
      await signup({
        shopName: fields.shopName.trim(),
        email,
        password: fields.password,
        phone: fields.phone.trim() || undefined,
        turnstileToken: challengeToken ?? undefined,
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SIGNUP_CLOSED') setClosedOnSubmit(true);
      if (err instanceof ApiError && (err.code === 'EMAIL_SEND_FAILED' || err.code === 'SIGNUP_PENDING')) setCreated(email);
      const challengeKey = err instanceof ApiError ? signupChallengeKey(err.code) : null;
      if (challengeKey) {
        const siteKey = err instanceof ApiError ? err.details?.turnstileSiteKey : undefined;
        if (typeof siteKey === 'string') setChallengeSiteKey(siteKey);
        setError(t(challengeKey));
      } else {
        setError(apiErrorMessage(err, t('auth.signup.failed'), t));
      }
      // A Turnstile token is single-use: whatever the refusal, the next press needs a fresh one.
      if (challengeToken || challengeKey) {
        setChallengeToken(null);
        setChallengeRound((round) => round + 1);
      }
      setSubmitting(false);
      return;
    }
    setCreated(email);
    setSubmitting(false);
  }

  const set = (key: keyof typeof fields) => (e: { target: { value: string } }) =>
    setFields((f) => ({ ...f, [key]: e.target.value }));

  return (
    <main className="login-screen">
      <section className="login-dialog" aria-labelledby="signup-title">
        <div className="login-dialog__heading">
          <img className="login-dialog__logo" src="/logo-rms.png" alt={t('brand.logoAlt')} width={456} height={194} />
          <h1 id="signup-title" className="login-dialog__title text-title">
            RMS Karaoke
          </h1>
          <p className="login-dialog__subtitle text-body">{t('auth.signup.subtitle')}</p>
        </div>

        {closed ? (
          <>
            <InlineAlert variant="info">{t('server.signupClosed')}</InlineAlert>
            <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/')}>
              {t('auth.signup.goToSignIn')}
            </Button>
          </>
        ) : created ? (
          <>
            {error && <InlineAlert variant="danger">{error}</InlineAlert>}
            <CheckEmail email={created} />
            <Button variant="ghost" fullWidth onClick={() => navigate('/')}>
              {t('auth.signup.goToSignIn')}
            </Button>
          </>
        ) : (
          <>
            {error && <InlineAlert variant="danger">{error}</InlineAlert>}
            <form className="login-dialog__form" onSubmit={handleSubmit}>
              <TextField
                label={t('auth.signup.shopName')}
                name="shopName"
                autoComplete="organization"
                autoFocus
                required
                minLength={2}
                maxLength={120}
                value={fields.shopName}
                onChange={set('shopName')}
              />
              <TextField
                label={t('auth.invite.email')}
                name="email"
                type="email"
                autoComplete="email"
                required
                value={fields.email}
                onChange={set('email')}
              />
              <TextField
                label={t('auth.invite.password')}
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN}
                placeholder={t('auth.passwordMinHint', { min: PASSWORD_MIN })}
                value={fields.password}
                onChange={set('password')}
              />
              <TextField
                label={t('auth.signup.phone')}
                name="phone"
                type="tel"
                autoComplete="tel"
                value={fields.phone}
                onChange={set('phone')}
              />
              {challengeSiteKey && (
                <TurnstileWidget key={challengeRound} siteKey={challengeSiteKey} onToken={setChallengeToken} />
              )}
              <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
                {t('auth.signup.submit')}
              </Button>
            </form>
            <Button variant="ghost" fullWidth onClick={() => navigate('/')}>
              {t('auth.signup.haveAccount')}
            </Button>
          </>
        )}
      </section>
    </main>
  );
}
